import {
  ApiError,
  DocumentQueue,
  DocumentStatus,
  DocumentSummary,
  ExtractedField
} from './types'

export type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'danger'

export const STATUS_META: Record<
  DocumentStatus,
  { label: string; tone: Tone; busy?: boolean }
> = {
  PENDING: { label: 'Queued', tone: 'neutral', busy: true },
  IN_PROGRESS: { label: 'Reading', tone: 'info', busy: true },
  RAW_RESPONSE_SAVED: { label: 'Validating', tone: 'info', busy: true },
  UNTRAINED_DOCUMENT: { label: 'Needs review', tone: 'warning' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'danger' }
}

export const statusMeta = (status?: string) =>
  STATUS_META[status as DocumentStatus] ?? {
    label: status ?? 'Unknown',
    tone: 'neutral' as Tone
  }

const STALLED_META = { label: 'Stalled', tone: 'warning' as Tone }

/** Status shown for a document: queued documents nobody is processing are "Stalled". */
export const documentStatusMeta = (
  doc?: Pick<DocumentSummary, 'status' | 'stalled'>
) => (doc?.stalled ? STALLED_META : statusMeta(doc?.status))

/** Document AI is actively working on it (so it is worth polling). */
export const isBusy = (doc?: Pick<DocumentSummary, 'status' | 'stalled'>) =>
  !!doc && !doc.stalled && !!statusMeta(doc.status).busy

/** Inbox tabs. `undefined` is the "All documents" view. */
export const QUEUE_TABS: {
  key: DocumentQueue | undefined
  label: string
  tone: Tone
}[] = [
  { key: 'NEEDS_REVIEW', label: 'Needs review', tone: 'warning' },
  { key: 'FAILED', label: 'Failed', tone: 'danger' },
  { key: 'PROCESSING', label: 'Processing', tone: 'info' },
  { key: 'COMPLETED', label: 'Completed', tone: 'success' },
  { key: undefined, label: 'All documents', tone: 'neutral' }
]

export const QUEUE_EMPTY: Record<string, { title: string; body: string }> = {
  NEEDS_REVIEW: {
    title: 'Nothing to review',
    body: 'Documents Document AI could not read with confidence will appear here.'
  },
  FAILED: {
    title: 'No failed documents',
    body: 'Documents that could not be processed will appear here so you can retry them.'
  },
  PROCESSING: {
    title: 'Nothing in progress',
    body: 'New documents show here while Document AI reads them.'
  },
  COMPLETED: {
    title: 'No completed documents yet',
    body: 'Processed documents and the data delivered from them will appear here.'
  },
  ALL: {
    title: 'No documents yet',
    body: 'Forward documents to the client’s inbox or upload PDFs to get started.'
  }
}

export const isQueue = (value: string | null): value is DocumentQueue =>
  value === 'PROCESSING' ||
  value === 'NEEDS_REVIEW' ||
  value === 'FAILED' ||
  value === 'COMPLETED'

// ── Formatting ──────────────────────────────────────────────────────────────

export const formatBytes = (bytes?: number) => {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const toDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export const formatDate = (value?: string) => {
  const date = toDate(value)
  if (!date) return value || '—'
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

export const formatDateTime = (value?: string) => {
  const date = toDate(value)
  if (!date) return value || '—'
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

/** ISO dates (YYYY-MM-DD) are formatted; text dates are shown as printed. */
export const formatDocumentDate = (value?: string) =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? formatDate(`${value}T00:00:00`)
    : value || '—'

export const formatAmount = (value?: number | null, currency?: string) => {
  if (value == null) return '—'
  const amount = value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return currency ? `${amount} ${currency}` : amount
}

/**
 * Reads amounts like "4,355.25", "$ 12.50" or "2'640.00" (the server's rule);
 * comma-decimal and other ambiguous text gives null.
 */
export const parseAmount = (text?: string | null) => {
  if (!text?.trim()) return null
  const kept = text.replace(/[^0-9.,'-]/g, '')
  if (/,\d{1,2}$/.test(kept)) return null
  const cleaned = kept.replace(/[,']/g, '')
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? Number(cleaned) : null
}

export const formatConfidence = (value?: number) =>
  value == null ? undefined : `${Math.round(value * 100)}%`

export const confidenceTone = (value?: number): Tone => {
  if (value == null) return 'neutral'
  if (value >= 0.9) return 'success'
  if (value >= 0.7) return 'warning'
  return 'danger'
}

/** "Jane Doe <jane@acme.com>" → { name: "Jane Doe", email: "jane@acme.com" } */
export const parseSender = (from?: string) => {
  if (!from) return { name: undefined, email: undefined }
  const match = from.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/)
  if (match) return { name: match[1] || undefined, email: match[2] }
  return { name: undefined, email: from.trim() }
}

/** Email bodies are converted to PDFs named email_body_<hash>.pdf. */
export const isEmailBody = (doc: Pick<DocumentSummary, 'fileName'>) =>
  /^email_body_[0-9a-f]+\.(pdf|html)$/i.test(doc.fileName ?? '')

export const displayFileName = (doc: DocumentSummary) =>
  isEmailBody(doc) ? 'Email body' : doc.fileName || `Document ${doc.id}`

/** Headline for a document: who it is from/for and its reference. */
export const documentTitle = (doc: DocumentSummary) => {
  const party = doc.supplierName || doc.payerName
  if (party && doc.reference) return `${party} · ${doc.reference}`
  return party || doc.subject || displayFileName(doc)
}

/** Groups extracted fields, keeping the order Document AI returned them in. */
export const groupFields = (fields: ExtractedField[]) => {
  const groups = new Map<string, ExtractedField[]>()
  fields.forEach((field) => {
    const list = groups.get(field.group) ?? []
    list.push(field)
    groups.set(field.group, list)
  })
  return Array.from(groups.entries()).map(([group, items]) => ({
    group,
    items
  }))
}

const isApiError = (value: unknown): value is ApiError =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  'detail' in value

/** A message for people from an RTK Query / axios error. */
export const errorMessage = (error: unknown, fallback: string) => {
  const e = error as {
    status?: number
    data?: unknown
    response?: { status?: number; data?: unknown }
  }
  const status = e?.status ?? e?.response?.status
  const data = e?.data ?? e?.response?.data
  if (isApiError(data)) return data.detail
  if (status === 401) {
    return 'Your session could not be verified by the server. Please refresh the page and try again.'
  }
  if (typeof data === 'string' && data.trim() && data.length < 300) return data
  return fallback
}

export const errorCode = (error: unknown) => {
  const e = error as { data?: unknown; response?: { data?: unknown } }
  const data = e?.data ?? e?.response?.data
  return isApiError(data) ? data.code : undefined
}
